<script>
  /**
   * ErrorBoundary.svelte - Error boundary component for Svelte
   *
   * Catches JavaScript errors in child components and displays a fallback UI.
   * Since Svelte doesn't have built-in error boundaries like React,
   * this component uses a try-catch pattern with error event handling.
   *
   * @example
   * <ErrorBoundary>
   *   <RiskyComponent />
   * </ErrorBoundary>
   *
   * @example
   * <ErrorBoundary onError={handleError} fallback={CustomFallback}>
   *   <MilkdownEditor />
   * </ErrorBoundary>
   */

  import { onMount, createEventDispatcher } from 'svelte';
  import { i18n } from '../stores/i18nStore';

  /** Optional error handler callback */
  export let onError = null;

  /** Component name for error reporting */
  export let componentName = 'Component';

  /** Whether to show technical details (useful for development) */
  export let showDetails = false;

  /** Whether to allow retry */
  export let allowRetry = true;

  const dispatch = createEventDispatcher();

  let hasError = false;
  let error = null;
  let errorInfo = null;
  let retryCount = 0;
  const MAX_RETRIES = 3;

  $: t = $i18n;

  /**
   * Handle caught error
   */
  function handleError(err, info = null) {
    hasError = true;
    error = err;
    errorInfo = info;

    // Log error with context
    console.error(`[ErrorBoundary] Error in ${componentName}:`, err);
    if (info) {
      console.error('[ErrorBoundary] Error info:', info);
    }

    // Call external error handler if provided
    if (onError && typeof onError === 'function') {
      onError(err, { componentName, errorInfo: info, retryCount });
    }

    // Dispatch error event
    dispatch('error', { error: err, componentName, errorInfo: info });
  }

  /**
   * Attempt to recover from error
   */
  function retry() {
    if (retryCount >= MAX_RETRIES) {
      return;
    }

    retryCount += 1;
    hasError = false;
    error = null;
    errorInfo = null;

    dispatch('retry', { retryCount });
  }

  /**
   * Reset error state (for external control)
   */
  export function reset() {
    hasError = false;
    error = null;
    errorInfo = null;
    retryCount = 0;
  }

  /**
   * Get error state (for external checks)
   */
  export function getErrorState() {
    return { hasError, error, errorInfo, retryCount };
  }

  // Listen for unhandled errors in window
  onMount(() => {
    const handleWindowError = (event) => {
      // Only catch errors that bubble up from our slot content
      if (event.error && !hasError) {
        handleError(event.error, { type: 'window-error', message: event.message });
        event.preventDefault();
      }
    };

    const handleUnhandledRejection = (event) => {
      if (!hasError) {
        handleError(event.reason || new Error('Unhandled promise rejection'), {
          type: 'unhandled-rejection'
        });
      }
    };

    window.addEventListener('error', handleWindowError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      window.removeEventListener('error', handleWindowError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  });
</script>

{#if hasError}
  <div class="error-boundary">
    <div class="error-content">
      <div class="error-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      </div>

      <h2 class="error-title">{t('errors.componentError')}</h2>

      <p class="error-message">
        {t('errors.componentErrorMessage', { component: componentName })}
      </p>

      {#if showDetails && error}
        <details class="error-details">
          <summary>{t('errors.technicalDetails')}</summary>
          <pre class="error-stack">{error.message}
{error.stack || ''}</pre>
        </details>
      {/if}

      <div class="error-actions">
        {#if allowRetry && retryCount < MAX_RETRIES}
          <button class="btn btn-primary" on:click={retry}>
            {t('errors.retry')} ({MAX_RETRIES - retryCount} {t('errors.attemptsLeft')})
          </button>
        {/if}

        <button class="btn btn-secondary" on:click={() => window.location.reload()}>
          {t('app.reload')}
        </button>
      </div>

      {#if retryCount >= MAX_RETRIES}
        <p class="error-max-retries">
          {t('errors.maxRetriesReached')}
        </p>
      {/if}
    </div>
  </div>
{:else}
  <slot />
{/if}

<style>
  .error-boundary {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    padding: var(--space-lg, 1.5rem);
    background: var(--error-background, #fef2f2);
    border: 1px solid var(--error-border, #fecaca);
    border-radius: var(--radius-md, 8px);
  }

  .error-content {
    text-align: center;
    max-width: 500px;
  }

  .error-icon {
    color: var(--error-color, #ef4444);
    margin-bottom: var(--space-md, 1rem);
  }

  .error-title {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--error-color, #ef4444);
    margin: 0 0 var(--space-sm, 0.5rem) 0;
  }

  .error-message {
    color: var(--text-secondary, #6b7280);
    margin: 0 0 var(--space-md, 1rem) 0;
    line-height: 1.5;
  }

  .error-details {
    text-align: left;
    margin-bottom: var(--space-md, 1rem);
  }

  .error-details summary {
    cursor: pointer;
    color: var(--text-muted, #9ca3af);
    font-size: 0.875rem;
    margin-bottom: var(--space-xs, 0.25rem);
  }

  .error-stack {
    background: var(--panel-background, #ffffff);
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: var(--radius-sm, 4px);
    padding: var(--space-sm, 0.5rem);
    font-size: 0.75rem;
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-word;
    max-height: 150px;
    overflow-y: auto;
    color: var(--text-primary, #1f2937);
  }

  .error-actions {
    display: flex;
    gap: var(--space-sm, 0.5rem);
    justify-content: center;
    flex-wrap: wrap;
  }

  .btn {
    padding: var(--space-sm, 0.5rem) var(--space-md, 1rem);
    border-radius: var(--radius-sm, 4px);
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
  }

  .btn-primary {
    background: var(--accent-color, #3b82f6);
    color: white;
  }

  .btn-primary:hover {
    background: var(--accent-color-hover, #2563eb);
  }

  .btn-secondary {
    background: var(--panel-background, #ffffff);
    color: var(--text-primary, #1f2937);
    border: 1px solid var(--border-color, #e5e7eb);
  }

  .btn-secondary:hover {
    background: var(--hover-background, #f9fafb);
  }

  .error-max-retries {
    margin-top: var(--space-md, 1rem);
    color: var(--error-color, #ef4444);
    font-size: 0.875rem;
  }
</style>
